import { ElementType, FC, PropsWithChildren, ReactNode } from 'react';
import { css, cx } from '@linaria/atomic';
import { Text } from '@frontend/design-system';

export interface GlassCardProps {
  className?: string;
  as?: ElementType;
}

interface GlassCardNamespace extends FC<PropsWithChildren<GlassCardProps>> {
  Header: typeof Header;
  Body: typeof Body;
}

export const GlassCard: GlassCardNamespace = ({ children, className, as: Component = 'div' }) => {
  return <Component className={cx(containerStyle, className)}>{children}</Component>;
};

export interface GlassCardHeaderProps {
  className?: string;
  icon?: ReactNode;
  title?: ReactNode;
  subTitle?: ReactNode;
}
const Header: FC<PropsWithChildren<GlassCardHeaderProps>> = ({ children, className, title, subTitle }) => {
  return (
    <div className={cx(headerStyle, className)}>
      {typeof title === 'string' ? (
        <Text as='h4' size='large' weight='bold'>
          {title}
        </Text>
      ) : (
        title
      )}
      {typeof subTitle === 'string' ? (
        <Text size='small' className={subTitleStyle}>
          {subTitle}
        </Text>
      ) : (
        subTitle
      )}
      {children}
    </div>
  );
};

export interface GlassCardBodyProps {
  className?: string;
  as?: ElementType;
}
const Body: FC<PropsWithChildren<GlassCardBodyProps>> = ({ className, children, as: Component = 'div' }) => {
  return <Component className={cx(bodyStyle, className)}>{children}</Component>;
};

GlassCard.Header = Header;
GlassCard.Body = Body;

const containerStyle = css`
  display: flex;
  flex-direction: column;
  row-gap: var(--fabric-space-gap-sm);
  column-gap: var(--fabric-space-gap-sm);
  border-top-left-radius: var(--fabric-radius-md);
  border-top-right-radius: var(--fabric-radius-md);
  border-bottom-right-radius: var(--fabric-radius-md);
  border-bottom-left-radius: var(--fabric-radius-md);
  border-top: 2px solid rgb(from var(--fabric-color-border-contrast) r g b / 50%);
  border-right: 2px solid rgb(from var(--fabric-color-border-contrast) r g b / 50%);
  border-bottom: 2px solid rgb(from var(--fabric-color-border-contrast) r g b / 50%);
  border-left: 2px solid rgb(from var(--fabric-color-border-contrast) r g b / 50%);
  padding-top: var(--fabric-space-padding-md);
  padding-right: var(--fabric-space-padding-md);
  padding-bottom: var(--fabric-space-padding-md);
  padding-left: var(--fabric-space-padding-md);
  backdrop-filter: blur(4px);
  background-color: rgb(from var(--fabric-color-surface-canvas) r g b / 50%);
  box-shadow: 0px 3px 8px 1px rgb(from var(--fabric-color-neutral-100) r g b / 15%);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  row-gap: var(--fabric-space-gap-md);
  column-gap: var(--fabric-space-gap-md);
`;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  background-color: var(--fabric-color-surface-canvas);
  border-top-left-radius: var(--fabric-radius-md);
  border-top-right-radius: var(--fabric-radius-md);
  border-bottom-right-radius: var(--fabric-radius-md);
  border-bottom-left-radius: var(--fabric-radius-md);
`;

const subTitleStyle = css`
  color: var(--fabric-color-text-muted);
`;
